import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Accordion,
  AccordionItem,
  Button,
  Select,
  ToggleSwitch,
  Tooltip,
} from '@enamelai/design-system'
import { Icon as InfoIcon } from '@enamelai/design-system/icons/Info'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useDashboardClinicId } from '@hooks/dashboard/useDashboardPeriod'
import {
  getDashboardSettings,
  saveDashboardSettings,
} from '@services/api/dashboard/dashboard-settings.api'
import {
  buildDefaultDashboardConfigs,
  DASHBOARD_DATE_RANGE_OPTIONS,
  DASHBOARD_PERSONAS,
  DASHBOARD_REFRESH_INTERVAL_OPTIONS,
  DashboardPersonaConfig,
} from './dashboardSettings.constants'
import {
  applyHiddenSectionsToConfig,
  buildHiddenSectionsFromConfig,
  DASHBOARD_SECTIONS_BY_TAB,
} from '@constants/Dashboard/dashboardSettingsApply.constants'
import { omitTemporarilyHiddenSections } from '@constants/Dashboard/huddleSectionVisibility.constants'
import { DashboardTab } from '@constants/Dashboard/dashboard.enums'
import { toDashboardSentenceCase } from '@utils/dashboardText.util'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import './DashboardStyle.css'

type DashboardConfigs = Record<string, DashboardPersonaConfig>

const DASHBOARD_LOADING_CONTAINER = '.settings-dashboard'
const DEFAULT_OPEN_SECTIONS = ['display', 'widgets']

type ToggleItemProps = {
  id: string
  label: string
  info?: string
  checked: boolean
  onChange: (checked: boolean) => void
}

const ToggleItem = ({
  id,
  label,
  info,
  checked,
  onChange,
}: ToggleItemProps): React.ReactElement => (
  <div className="settings-dashboard-toggle">
    <ToggleSwitch
      id={id}
      aria-label={label}
      checked={checked}
      onChange={onChange}
    />
    <label htmlFor={id} className="settings-dashboard-toggle-label">
      {label}
    </label>
    {info ? (
      <Tooltip content={info} portal>
        <span className="settings-dashboard-info-icon" aria-label={info}>
          <InfoIcon aria-hidden="true" />
        </span>
      </Tooltip>
    ) : null}
  </div>
)

const DASHBOARD_SECTION_TABS = [
  { id: DashboardTab.Overview, label: 'Overview' },
  { id: DashboardTab.Huddle, label: 'Huddle' },
  { id: DashboardTab.Operations, label: 'Operations' },
  { id: DashboardTab.Finance, label: 'Finance' },
  { id: DashboardTab.Performance, label: 'Performance' },
] as const

function sectionsForSettingsTab(tab: string): string[] {
  return omitTemporarilyHiddenSections(
    tab,
    DASHBOARD_SECTIONS_BY_TAB[tab] ?? [],
  )
}

const Dashboard = (): React.ReactElement => {
  const { setHeaderButtons } = useSettingsHeader()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const clinicId = useDashboardClinicId()

  const [savedConfigs, setSavedConfigs] = useState<DashboardConfigs>(() =>
    buildDefaultDashboardConfigs(),
  )
  const [configs, setConfigs] = useState<DashboardConfigs>(() =>
    buildDefaultDashboardConfigs(),
  )
  const [saving, setSaving] = useState(false)
  const [dataReady, setDataReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [personaId, setPersonaId] = useState(DASHBOARD_PERSONAS[0].id)
  useSettingsSubpageLoading(!dataReady)

  const editingConfig = configs[personaId]
  const hiddenSections = useMemo(
    () => buildHiddenSectionsFromConfig(editingConfig),
    [editingConfig],
  )

  const isDirty = useMemo(
    () => JSON.stringify(configs) !== JSON.stringify(savedConfigs),
    [configs, savedConfigs],
  )

  useEffect(() => {
    if (!clinicId) {
      setError('No clinic selected')
      setDataReady(true)
      return
    }

    let cancelled = false
    setError(null)
    setDataReady(false)

    getDashboardSettings({ clinicId })
      .then((map) => {
        if (cancelled) return
        setSavedConfigs(map)
        setConfigs(structuredClone(map))
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(
          e instanceof Error ? e.message : 'Failed to load dashboard settings',
        )
      })
      .finally(() => {
        if (!cancelled) setDataReady(true)
      })

    return () => {
      cancelled = true
    }
  }, [clinicId])

  const updateDisplayPreference = <
    K extends keyof DashboardPersonaConfig['display'],
  >(
    key: K,
    value: DashboardPersonaConfig['display'][K],
  ): void => {
    setConfigs((prev) => ({
      ...prev,
      [personaId]: {
        ...prev[personaId],
        display: { ...prev[personaId].display, [key]: value },
      },
    }))
  }

  const toggleSection = (tab: string, name: string, visible: boolean): void => {
    const current = configs[personaId]
    if (!current) return
    const hidden = buildHiddenSectionsFromConfig(current)
    const next: Record<string, Set<string>> = {}
    Object.entries(hidden).forEach(([key, set]) => {
      next[key] = new Set(set)
    })
    const sectionSet = new Set(next[tab] ?? [])
    if (visible) sectionSet.delete(name)
    else sectionSet.add(name)
    next[tab] = sectionSet
    const updated = applyHiddenSectionsToConfig(current, next)
    setConfigs((prev) => ({
      ...prev,
      [personaId]: updated,
    }))
  }

  const handleDiscard = useCallback((): void => {
    setConfigs(structuredClone(savedConfigs))
  }, [savedConfigs])

  const handleSave = useCallback(async (): Promise<void> => {
    if (!clinicId) {
      showToast(
        createFailedToast(
          'Unable to save dashboard',
          'No clinic is selected. Please select a clinic and try again.',
        ),
      )
      return
    }

    setSaving(true)
    showLoading({ container: DASHBOARD_LOADING_CONTAINER })
    try {
      const saved = await saveDashboardSettings({
        clinicId,
        settings: configs,
      })
      setSavedConfigs(saved)
      setConfigs(structuredClone(saved))
      showToast(
        createSuccessToast(
          'Dashboard saved',
          'Dashboard preferences have been updated successfully.',
        ),
      )
    } catch (e: unknown) {
      showToast(
        createFailedToast(
          'Unable to save dashboard',
          e instanceof Error
            ? e.message
            : 'Something went wrong while saving. Please try again.',
        ),
      )
    } finally {
      setSaving(false)
      hideLoading(DASHBOARD_LOADING_CONTAINER)
    }
  }, [clinicId, configs, hideLoading, showLoading, showToast])

  useEffect(() => {
    setHeaderButtons(
      <>
        <Button
          className="settings-ds-button"
          variant="secondary"
          size="md"
          type="button"
          onClick={handleDiscard}
          disabled={!isDirty || saving}
        >
          Cancel
        </Button>
        <Button
          className="settings-ds-button"
          variant="primary"
          size="md"
          type="button"
          onClick={() => void handleSave()}
          disabled={!isDirty || saving}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </>,
    )

    return () => setHeaderButtons(null)
  }, [handleDiscard, handleSave, isDirty, saving, setHeaderButtons])

  if (error) {
    return (
      <div className="settings-dashboard">
        <p className="settings-dashboard-error" role="alert">
          {error}
        </p>
      </div>
    )
  }

  return (
    <div className="settings-dashboard">
      <section className="settings-dashboard-persona">
        <h3 className="settings-dashboard-heading">Persona</h3>
        <div
          className="settings-dashboard-persona-grid"
          role="radiogroup"
          aria-label="Persona"
        >
          {DASHBOARD_PERSONAS.map((persona) => {
            const selected = persona.id === personaId
            return (
              <button
                type="button"
                key={persona.id}
                role="radio"
                aria-checked={selected}
                className={`settings-dashboard-persona-tile${
                  selected ? ' is-selected' : ''
                }`}
                onClick={() => setPersonaId(persona.id)}
              >
                <span className="settings-dashboard-persona-name">
                  {persona.name}
                </span>
                {selected ? (
                  <span className="settings-dashboard-persona-selected">
                    Selected
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>
      </section>

      <Accordion
        className="settings-dashboard-accordion"
        allowMultiple
        defaultOpenIds={DEFAULT_OPEN_SECTIONS}
      >
        <AccordionItem id="display" title="Display preferences">
          <div className="settings-dashboard-fields">
            <Select
              label="Default date range"
              options={DASHBOARD_DATE_RANGE_OPTIONS}
              value={editingConfig.display.dateRange}
              onChange={(value) =>
                updateDisplayPreference('dateRange', String(value))
              }
            />
            <Select
              label="Auto-refresh interval"
              options={DASHBOARD_REFRESH_INTERVAL_OPTIONS}
              value={editingConfig.display.refreshInterval}
              onChange={(value) =>
                updateDisplayPreference('refreshInterval', String(value))
              }
            />
          </div>
          <div className="settings-dashboard-display-toggles">
            <ToggleItem
              id="dashboard-show-goal-progress"
              label="Show goal progress on widgets"
              info="Displays a progress bar against monthly/quarterly targets"
              checked={editingConfig.display.showGoalProgress}
              onChange={(checked) =>
                updateDisplayPreference('showGoalProgress', checked)
              }
            />
            <ToggleItem
              id="dashboard-show-alert-banners"
              label="Show actionable alert banners"
              info="Highlights urgent items like unconfirmed appointments or overdue A/R"
              checked={editingConfig.display.showAlertBanners}
              onChange={(checked) =>
                updateDisplayPreference('showAlertBanners', checked)
              }
            />
          </div>
        </AccordionItem>

        <AccordionItem id="widgets" title="Visible sections">
          {DASHBOARD_SECTION_TABS.map((tab) => {
            const sections = sectionsForSettingsTab(tab.id)
            if (sections.length === 0) return null
            return (
              <div key={tab.id} className="settings-dashboard-widget-group">
                <h4 className="settings-dashboard-widget-group-title">
                  {tab.label}
                </h4>
                <div className="settings-dashboard-widget-grid">
                  {sections.map((name) => (
                    <ToggleItem
                      key={name}
                      id={`dashboard-section-${tab.id}-${name}`}
                      label={toDashboardSentenceCase(name)}
                      info="Shown on this dashboard tab when enabled"
                      checked={!(hiddenSections[tab.id] ?? new Set()).has(name)}
                      onChange={(checked) =>
                        toggleSection(tab.id, name, checked)
                      }
                    />
                  ))}
                </div>
              </div>
            )
          })}
        </AccordionItem>
      </Accordion>
    </div>
  )
}

export default Dashboard
